import { z } from "zod";

import {
	campaignStatusInfoSchema,
	campaignSummarySchema,
	companyOtherListSchema,
	FindPersonContactMode,
	leadLabelsSchema,
} from "@signalist/shared-types";

import { MetaDataSchema } from "@/schemas/meta-data";

import { ERROR_TYPE } from "@/utils";

import {
	AiApplyType,
	AssignStatus,
	AsyncProcessingStatus,
	AsyncProcessingType,
	CustomColumnTypes,
	EnrichmentType,
	GetContactInformation,
	GetEmailInformation,
	GetPhoneInformation,
	InclusionFilterStatus,
	InclusionStatus,
	JobChangeStatus,
	ListAction,
	ListProcessingStatus,
	ListRecurrence,
	ListSourceType,
	ListStatus,
	ListType,
	PriorityFilterStatus,
	ResponseStatus,
	SearchBase,
	SmartListFrequency,
	UpdateType,
} from "@/enum/list.enum";

import { AiEnrichmentTarget } from "@/enum/ai-enrichment.enum";

import {
	CopywritingStatus,
	GenerateCopywritingResponseSchema,
} from "@/pages/Private/pages/Messages/components/GenerateBar/types";

import { ApolloType } from "@/enum/apollo.enum";

import { LiProfileType } from "@/enum/liprofile.enum";

import { LiCompanyActivityType } from "@/enum/licompanyactivity.enum";

import { ScrapeLevel } from "@/enum/job.enum";

import { CsvEnrichmentType } from "@/enum/csv-enrichment.enum";

import { CustomEventType } from "@/enum/custom-event.enum";

import { WebhookType } from "@/enum/webhook.enum";

import { CampaignStatus } from "@/enum/campaign-status.enum";

import { ListCountsSchema } from "./list-counts";

import {
	ArchiveResponseSchema,
	BulkAssignResponseSchema,
	LemlistSchema,
	NorthdataSchema,
} from "./listResponses";

import { CronJobSchema, TrackerFileInfoSchema, TrackerInfoSchema } from "./list-tracker.schema";

import {
	ApolloCreatedSchema,
	ApolloSearchResultPersonSchema,
} from "../../Apollo/schema/get-apollos";
import { PeopleSchema } from "../../Apollo/schema/get-people";
import { ListPersonScheduleSchema, type ListPersonSchedule } from "./list-person-schedule.schema";
import { SenderSchema } from "./sender.schema";
import { EventOutreachSchema } from "./event-outreach.schema";
import { WebhookSchema } from "../../Company/pages/WebhookOverview/schema/webhook";
import { CsvEnrichmentSchema } from "../../CsvEnrichment/schema/csvEnrichment";
import { CustomEventSchema } from "../../CustomEvent/schema/customEvent";
import { FundingMaCompanyPersonSchema, FundingMaSchema } from "../../FundingMa/schema/funding-ma";
import { JobSchema } from "../../Job/schema/job";
import { OfferSchema } from "../../Job/schema/offer";
import { LiCompanyActivitySchema } from "../../LiCompanyActivity/schema/liCompanyActivity";
import { LiEventSchema } from "../../LiEvent/schema/liEvent";
import { LiEventEntrySchema } from "../../LiEvent/schema/liEventEntry";
import { LiPeopleSchema } from "../../LiPeople/schema/liPeople";
import { LiPostSchema } from "../../LiPost/schema/liPost";
import { LiProfilePostsSchema } from "../../LiProfile/schema/liProfile";
import { LiProfileVisitsSchema } from "../../LiProfileVisits/schema/liProfileVisits";
import { LiSnSchema } from "../../LiSn/schema/liSn";
import { MapSchema } from "../../Map/schema/map";
import { MapBusinessSchema } from "../../Map/schema/mapBusiness";
import { AiEnrichmentResultSchema, OpenAiSchema } from "../../OpenAi/schema/openAi";
import { TrackerSignalResultSchema } from "../../ProfileTracker/schema/trackerSignalResult";
import { ScoreSchema } from "../../Score/schema/score.schema";
import { SwarmSchema } from "../../Swarm/schema/swarm";
import { CompanyEnrichSchema } from "../../CompanyEnrich/schema/companyEnrich";
import { UserSchema } from "../../Users/schema/invite-user";
import { HubspotDealCreationTriggerWithInherit } from "../../Company/schema/hubspot";

// Type-only import: keep this schema module free of the UnassignedTable component's runtime
// graph (redux store → search slice), which would otherwise create a circular import with
// search.schema.ts (it imports ListSchema from here).
import { ListPersonCampaignSchema } from "./listPersonCampaign";
import { ColumnConfigSchema } from "./columnConfig";
import { ColumnValueSchema } from "./columnValue";
import { ParallelAiSchema } from "../../ParallelAi/schema/parallelAi";
import { ListIntervalSettingsSchema } from "./list-interval-settings.schema";
import { ListEmailDeliverabilityOverridesSchema } from "./list-email-deliverability.schema";

// The bulk status-mutation request schemas live in their own module; re-exported so the existing
// `schema/list` import sites keep working.
export * from "./bulk-change-status";

const { REQUIRED } = ERROR_TYPE;

export { ArchiveResponseSchema, BulkAssignResponseSchema, LemlistSchema, NorthdataSchema };

// The interval fields and their mode enum live in a sibling file (PREC-4261); re-exported here.
export { ListIntervalSettingsSchema, SignalIntervalMode } from "./list-interval-settings.schema";

// Smart-list AI recommendation schemas now live in a sibling file (to keep this
// module within the file-length ratchet); re-exported so consumers can keep
// importing them from "@/pages/Private/pages/List/schema/list".
export {
	ListCreateRecommendationRecurrenceSchema,
	ListCreateRecommendationRequirementSchema,
	ListCreateRecommendationAvailableTypeSchema,
	ListCreateRecommendationRequestSchema,
	ListCreateRecommendationItemSchema,
	ListCreateRecommendationResponseSchema,
} from "./list-recommendation.schema";
export type {
	ListCreateRecommendationAvailableType,
	ListCreateRecommendationRequest,
	ListCreateRecommendationResponse,
} from "./list-recommendation.schema";

export const LemlistAllSchema = z.array(LemlistSchema);

export const CustomColumnSchema = z.object({
	id: z.number().optional(),
	columnId: z.number().optional(),
	customColumnName: z.string().refine((val) => !val.includes("_"), {
		message: "COLUMN_NAME_NO_UNDERSCORE",
	}),
	customColumnType: z.nativeEnum(CustomColumnTypes),
	prompt: z.string().optional(),
	value: z
		.string()
		.or(z.number().or(z.boolean().or(z.array(z.string()).or(z.any()))))
		.nullable()
		.optional(),
	sourceConfig: z
		.string()
		.or(z.number().or(z.boolean().or(z.array(z.string()).or(z.any()))))
		.nullable()
		.optional(),
	saveAsTemplate: z.boolean().optional(),
	online: z.boolean().optional(),
	single: z.boolean().optional(),
	locale: z.any().optional(),
});

// Event-outreach and cron/tracker schemas live in sibling files; re-exported here so existing
// `from ".../schema/list"` imports (notably the EventOutreachSchemaType) keep working.
export * from "./event-outreach.schema";
export * from "./list-tracker.schema";

export const ListSchema = z.object({
	id: z.number(),
	lastRunAt: z.string().nullable(),
	name: z.string().min(4, { message: REQUIRED }).trim(),
	refreshEveryXDays: z.number().nullable(),
	maxProspects: z.number().nullable(),
	onlyNewLeads: z.boolean(),
	findPersonContactMode: z.nativeEnum(FindPersonContactMode).optional(),
	apollos: z.array(ApolloCreatedSchema).optional(),
	liEvents: z.array(LiEventSchema).optional(),
	liPeople: z.array(LiPeopleSchema).optional(),
	liCompanyActivity: z.array(LiCompanyActivitySchema).optional(),
	liSn: z.array(LiSnSchema).optional(),
	liProfileVisits: z.array(LiProfileVisitsSchema).optional(),
	liProfile: z.array(LiProfilePostsSchema).optional(),
	parallelAi: z.array(ParallelAiSchema).optional(),
	liPosts: z.array(LiPostSchema).optional(),
	fundingMa: z.array(FundingMaSchema).optional(),
	swarms: z.array(SwarmSchema).optional(),
	companyEnriches: z.array(CompanyEnrichSchema).optional(),
	processingStatus: z.nativeEnum(ListProcessingStatus),
	aiEnrichments: z.array(OpenAiSchema).optional(),
	csvEnrichments: z.array(CsvEnrichmentSchema).optional(),
	customEvents: z.array(CustomEventSchema).optional(),
	maps: z.array(MapSchema).optional(),
	prospects: z.array(ApolloSearchResultPersonSchema).optional(),
	searchBase: z.nativeEnum(SearchBase),
	jobs: z.array(JobSchema).optional(),
	scores: z.array(ScoreSchema).optional(),
	lemlists: z.array(LemlistSchema).optional(),
	status: z.nativeEnum(ListStatus),
	found: z.number().optional().nullable(),
	processed: z.number().optional().nullable(),
	savedForLater: z.number().optional().nullable(),
	assignedLeads: z.number().optional().nullable(),
	// Surface-relevant lead counts returned by the lean endpoint for the messaging scope
	// filter: `relevantLeads` matches the page's assignStatus set (e.g. Inbox replies),
	// `totalLeads` is every included lead. See ListScopeFilter / list-query.repository.
	relevantLeads: z.number().optional().nullable(),
	totalLeads: z.number().optional().nullable(),
	lastAddedAt: z.string().optional().nullable(),
	latestUpdateAt: z.string().optional().nullable(),
	sourceType: z.nativeEnum(ListSourceType),
	customColumns: z
		.record(
			z.string(),
			z.object({ type: z.nativeEnum(CustomColumnTypes), value: z.string().optional() })
		)
		.nullable(),
	columns: z.array(ColumnConfigSchema).optional(),
	type: z.nativeEnum(ListType),
	recurrence: z.nativeEnum(ListRecurrence).optional(),
	// PREC-3908: the scheduler's own next run, so the overview can state when a list runs next
	// instead of assuming "tomorrow". Null/absent whenever nothing is scheduled.
	nextRunAt: z.string().nullable().optional(),
	iteration: z.number(),
	updatedAt: z.string().transform((arg) => new Date(arg)),
	updatedBy: UserSchema.optional().nullable(),
	scrapeLevel: z.nativeEnum(ScrapeLevel).nullable().optional(),
	isNewCreationProcess: z.boolean().optional(),
	// True while the list name is still the auto-generated default; the settings step pre-fills
	// the name field with the first source's name on the first visit when this is set.
	nameAutoGenerated: z.boolean().optional(),
	...ListCountsSchema.shape,
	archived: z.boolean().optional(),
	entityType: z
		.nativeEnum(CsvEnrichmentType)
		.or(z.nativeEnum(CustomEventType))
		.nullable()
		.optional(),
	enrichmentType: z.nativeEnum(EnrichmentType).nullable().optional(),
	processingStatusMessage: z.string().nullable().optional(),
	requiresProcessingRestart: z.boolean().optional(),
	processingProgressPercent: z.number().nullable().optional(),
	lastCronJob: CronJobSchema.optional(),
	liveViewUrl: z.string().nullable().optional(),
	maxDate: z.string().nullable().optional(),
	getContactInformation: z.nativeEnum(GetContactInformation),
	getEmailInformation: z.nativeEnum(GetEmailInformation),
	getPhoneInformation: z.nativeEnum(GetPhoneInformation),
	webhooks: z.array(WebhookSchema),
	campaignConfig: z.any().nullable().optional(),
	priority: z.number().nullable().optional(),
	campaignStatus: z.nativeEnum(CampaignStatus).nullable().optional(),
	campaignCount: z.number().nullable().optional(), // lean overview: drives the expand chevron
	campaignSummary: z.array(campaignSummarySchema).nullable().optional(), // folded into lean overview

	// Per-campaign derived display status + lock, keyed by campaign uuid (PREC-3249).
	campaignStatuses: z.record(z.string(), campaignStatusInfoSchema).nullable().optional(),
	// Event-outreach details (PREC-3520) — null when the list has no event configured.
	event: EventOutreachSchema.nullable().optional(),
	specialType: z.string().nullable().optional(),
	...ListIntervalSettingsSchema.shape,
	// Per-list email deliverability overrides (PREC-4261); null = workspace default
	...ListEmailDeliverabilityOverridesSchema.shape,
	// Tracker information
	trackers: z.array(TrackerInfoSchema).optional(),
	// Signal configuration (for TRACKER lists)
	signalConfig: z
		.object({
			id: z.number().nullable().optional(),
			listId: z.number().optional(),
			deepResearchPrompt: z.string().optional(),
			systemPrompt: z.string().optional(),
			leadUpdatesPrompt: z.string().optional(),
			companyUpdatesPrompt: z.string().optional(),
			industryUpdatesPrompt: z.string().optional(),
			checkLinkedinPosts: z.boolean().optional(),
			analyzeLinkedinEngagement: z.boolean().optional(),
			checkTechStackChanges: z.boolean().optional(),
			createdAt: z.string().optional(),
			updatedAt: z.string().optional(),
		})
		.nullable()
		.optional(),
	// HubSpot deal creation trigger (list-level override)
	hubspotDealCreationTrigger: z
		.nativeEnum(HubspotDealCreationTriggerWithInherit)
		.nullable()
		.optional(),
	// Auto-assign to campaign after lead scoring
	autoAssignToCampaign: z.boolean().optional(),
	autoAssignMinScore: z.string().nullable().optional(),
	// Smart List fields
	smartList: z
		.object({
			frequency: z.nativeEnum(SmartListFrequency).optional(),
			filter: z.string().nullable().optional(),
			semanticSearchProspects: z.string().nullable().optional(),
			sources: z
				.array(
					z.object({
						id: z.number(),
						name: z.string(),
						filter: z.string().nullable().optional(),
						lastRunAt: z.string().nullable().optional(),
					})
				)
				.optional(),
		})
		.nullable()
		.optional(),
});

export const ListLogSchema = z.object({
	id: z.number(),
	list: ListSchema,
	action: z.nativeEnum(ListAction),
	description: z.string().nullable(),
	iteration: z.number(),
	data: z.any().nullable(),
	updatedAt: z.string(),
	createdAt: z.string(),
});

export const ListSourceSchema = z.object({
	id: z.number(),
	name: z.string().min(4, { message: REQUIRED }).trim(),
	sourceType: z.nativeEnum(ListSourceType),
	type: z.nativeEnum(ApolloType).optional(),
});

export const ExclusionReasonSchema = z.object({
	id: z.number(),
	reason: z.string(),
	filterParam: z.string().nullable(),
	filterValue: z.string().nullable(),
	createdAt: z.string().transform((arg) => new Date(arg)),
	updatedAt: z.string().transform((arg) => new Date(arg)),
});

export const ListPersonOtherListsSchema = z.object({
	list: ListSchema,
	assignStatus: z.nativeEnum(AssignStatus),
	inclusionStatus: z.nativeEnum(InclusionStatus),
});

export const ListEventSchema = z.object({
	id: z.number(),
	aggregateId: z.string(),
	aggregateType: z.string(),
	companyId: z.number().nullable(),
	createdAt: z.string(),
	error: z.string().nullable(),
	eventType: z.string(),
	failedAt: z.string().nullable(),
	jobId: z.string().nullable(),
	processedAt: z.string().nullable(),
	publishedAt: z.string(),
	scheduledFor: z.string().nullable(),
	scheduler: z.string().nullable(),
	updatedAt: z.string(),
});

// ListPersonScheduleSchema lives in ./list-person-schedule.schema (imported + re-exported at the top
// of this file) to keep this file within the length ratchet; it is used below to type the schedules
// array.
export { ListPersonScheduleSchema };
export type { ListPersonSchedule };

// SenderSchema lives in ./sender.schema (imported + re-exported here) to keep this file within the
// length ratchet; it is used below to type the `senders` array.
export { SenderSchema };

export const ListPersonSchema = z.object({
	id: z.number(),
	name: z.string().trim().optional(),
	createdAt: z.string().transform((arg) => new Date(arg)),
	updatedAt: z.string().transform((arg) => new Date(arg)),
	assignStatus: z.nativeEnum(AssignStatus),
	plannedAssignStatus: z.nativeEnum(AssignStatus).nullable(),
	responseStatus: z.nativeEnum(ResponseStatus),
	inclusionStatus: z.nativeEnum(InclusionStatus),
	exclusionReason: z.string().nullable().optional(),
	exclusionReasons: z.array(ExclusionReasonSchema).nullable().optional(),
	companyPeopleCheck: z
		.object({
			assignedAllTime: z.number(),
			assignedOnThisList: z.number(),
			notAssignedOnThisList: z.number(),
		})
		.nullable()
		.optional(),
	activities: z
		.array(
			z.object({
				activity: z.string(),
				executedAt: z.string().transform((arg) => new Date(arg)),
				status: z.string().optional(),
				reply: z.string().nullable(),
				message: z.string().nullable(),
			})
		)
		.optional(),
	list: ListSchema,
	events: z.array(ListEventSchema).optional(),
	prospect: PeopleSchema.optional(),
	score: z
		.array(
			z.object({
				id: z.number(),
				name: z.string(),
				scoreValue: z.number(),
			})
		)
		.optional(),
	lemlist: LemlistSchema.optional(),
	userAssigned: UserSchema.optional(),
	aiEnrichmentResultPerson: z.array(AiEnrichmentResultSchema).optional(),
	mapBusiness: MapBusinessSchema.optional(),
	offer: OfferSchema.optional(),
	aiEnrichmentResultOrg: z.array(AiEnrichmentResultSchema).optional(),
	additionalInformation: z.any().nullable(),
	blacklistType: z.string().nullable().optional(),
	/**
	 * Why the lead is blocked, for the Blacklist bucket's columns (PREC-3594). Null unless a
	 * blacklist entry matches the lead — a list-only exclusion has no entry to describe.
	 */
	blacklistDetail: z
		.object({
			origin: z.string().nullable(),
			detectedBy: z.string().nullable(),
			reason: z.string().nullable(),
			addedBy: z.string().nullable(),
			addedAt: z.coerce.date().nullable(),
		})
		.nullable()
		.optional(),
	otherLists: z.array(ListPersonOtherListsSchema).optional(),
	// PREC-2750: null when the lead's company is not identifiable (no organization).
	companyOtherLists: z.array(companyOtherListSchema).nullable().optional(),
	otherEvents: z.array(LiEventEntrySchema).nullable().optional(),
	liEventEntry: LiEventEntrySchema.optional(),
	jobs: z.array(JobSchema).optional(),
	customColumns: z.record(z.string(), z.string().or(z.number().or(z.boolean()))).nullable(),
	connections: z.array(z.string()).optional(),
	// Lead "color labels" computed server-side, in display-priority order (see @signalist/shared-types).
	labels: leadLabelsSchema.optional(),
	columns: z.array(ColumnValueSchema),
	liPostResult: z
		.object({
			contentDate: z.date().nullable(),
			content: z.string().nullable(),
			contentUrl: z.string().nullable(),
			comments: z.number().nullable(),
			likes: z.number().nullable(),
			shares: z.number().nullable(),
			contentResponse: z.string().nullable(),
			contentResponseUrl: z.string().nullable(),
			type: z.nativeEnum(LiProfileType).nullable(),
			authorName: z.string().nullable(),
			authorFirstName: z.string().nullable(),
			authorLastName: z.string().nullable(),
			authorImage: z.string().nullable(),
			authorLinkedinUrl: z.string().nullable(),
			authorCompanyName: z.string().nullable(),
			authorCompanyLinkedinUrl: z.string().nullable(),
			authorCompanyImage: z.string().nullable(),
		})
		.optional(),
	liCompanyActivityEntryResult: z
		.object({
			contentDate: z.string().nullable(),
			content: z.string().nullable(),
			contentUrl: z.string().nullable(),
			contentResponse: z.string().nullable(),
			contentResponseUrl: z.string().nullable(),
			type: z.nativeEnum(LiCompanyActivityType).nullable(),
			liCompanyActivityEntry: z.object({
				id: z.number(),
				liCompanyActivityId: z.number(),
				linkedinUrl: z.string(),
				liCompanyActivity: z.object({
					id: z.number(),
					name: z.string(),
					linkedinUrl: z.string(),
				}),
			}),
		})
		.optional(),
	liProfileEntryResult: z
		.object({
			contentDate: z.date().nullable(),
			content: z.string().nullable(),
			contentUrl: z.string().nullable(),
			contentResponse: z.string().nullable(),
			contentResponseUrl: z.string().nullable(),
			type: z.nativeEnum(LiProfileType).nullable(),
		})
		.optional(),
	sourceName: z.string().nullable(),
	branchesInEasternGermany: z.number().optional(),
	branchesInEuropeanUnion: z.number().optional(),
	branchesInSaxony: z.number().optional(),
	branchesWorldwide: z.number().optional(),
	cronJobs: z.array(CronJobSchema),
	addedToCampaignAt: z.string().transform((arg) => new Date(arg)),
	stopCampaignAt: z.string().nullable().optional(),
	...ListPersonCampaignSchema.shape,
	schedules: z.array(ListPersonScheduleSchema).optional(),
	copywritingStatus: z.nativeEnum(CopywritingStatus).nullable().optional(),
	copywritingEventId: z.number().nullable().optional(),
	copywritingOutput: GenerateCopywritingResponseSchema.nullable().optional(),
	copywritingError: z.string().nullable().optional(),
	copywritingGeneratedAt: z.string().nullable().optional(),
	asyncProcessingStatus: z.nativeEnum(AsyncProcessingStatus).nullable().optional(),
	asyncProcessingType: z.nativeEnum(AsyncProcessingType).nullable().optional(),
	lastConversationAt: z
		.string()
		.transform((arg) => new Date(arg))
		.nullable()
		.optional(),
	reply1: z.string().nullable().optional(),
	reply2: z.string().nullable().optional(),
	reply3: z.string().nullable().optional(),
	replyDate1: z.number().nullable().optional(),
	replyDate2: z.number().nullable().optional(),
	replyDate3: z.number().nullable().optional(),
	replyReasoning1: z.string().nullable().optional(),
	replyReasoning2: z.string().nullable().optional(),
	replyReasoning3: z.string().nullable().optional(),
	fundingMaCompanyPerson: FundingMaCompanyPersonSchema.optional(),
	// Job Change tracking fields
	jobChangeStatus: z.nativeEnum(JobChangeStatus).nullable().optional(),
	jobChangeLastCheckedAt: z
		.string()
		.transform((arg) => new Date(arg))
		.nullable()
		.optional(),
	jobChangeDetectedAt: z
		.string()
		.transform((arg) => new Date(arg))
		.nullable()
		.optional(),
	// Tracker signal result for TRACKER list type
	trackerSignalResult: TrackerSignalResultSchema.nullable().optional(),
	// Campaign step tracking fields
	lastNodeType: z.string().nullable().optional(),
	lastScheduledAt: z
		.string()
		.transform((arg) => new Date(arg))
		.nullable()
		.optional(),
	senders: z.array(SenderSchema).nullable().optional(),
});

export const ListAiEnrichmentSchema = z.object({
	id: z.number(),
	name: z.string().min(4, { message: REQUIRED }).trim(),
	aiEnrichmentTarget: z.nativeEnum(AiEnrichmentTarget),
});

export const CreateListSchema = z
	.object({
		name: z.string().min(4, { message: REQUIRED }).trim(),
		sourceType: z.nativeEnum(ListSourceType),
		searchBase: z.nativeEnum(SearchBase),
	})
	.refine((data) => {
		if (
			![ListSourceType.JOBS, ListSourceType.MAPS, ListSourceType.PARALLEL_AI].includes(
				data.sourceType
			) &&
			data.searchBase === SearchBase.SOURCE
		) {
			return false;
		}

		return true;
	}, "REQUIRED");

export const ListWebhookSchema = z.object({
	id: z.number().optional(),
	listId: z.number(),
	webhookId: z.number(),
	type: z.nativeEnum(WebhookType),
});

export const SaveListSettingsSchema = z.object({
	name: z.string().min(4, { message: REQUIRED }).trim(),
	searchBase: z.nativeEnum(SearchBase),
	sourceType: z.nativeEnum(ListSourceType),
	type: z.nativeEnum(ListType),
	recurrence: z.nativeEnum(ListRecurrence).optional(),
	maxProspects: z.string(),
	maxPerCompany: z.string(),
	campaigns: z.array(z.number()),
	onlyNewLeads: z.boolean(),
	findPersonContactMode: z.nativeEnum(FindPersonContactMode).optional(),
	getContactInformation: z.nativeEnum(GetContactInformation),
	getEmailInformation: z.nativeEnum(GetEmailInformation),
	getPhoneInformation: z.nativeEnum(GetPhoneInformation),
	maxDate: z.string().nullable().optional(),
	webhooks: z.array(ListWebhookSchema),
	priority: z.number().nullable().optional(),
	...ListIntervalSettingsSchema.shape,
	// Auto-assign to campaign after lead scoring
	autoAssignToCampaign: z.boolean().optional(),
	autoAssignMinScore: z.string().nullable().optional(),
	// Smart List fields
	smartList: z
		.object({
			frequency: z.nativeEnum(SmartListFrequency).optional(),
			filter: z.string().nullable().optional(),
			semanticSearchProspects: z.string().nullable().optional(),
			sources: z
				.array(
					z.object({
						id: z.number(),
						name: z.string(),
						filter: z.string().nullable().optional(),
						lastRunAt: z.string().nullable().optional(),
					})
				)
				.optional(),
		})
		.nullable()
		.optional(),
});

export const SaveListSettingsValidationSchema = z
	.object({
		name: z.string().min(4, { message: REQUIRED }).trim(),
		searchBase: z.nativeEnum(SearchBase),
		type: z.nativeEnum(ListType),
		recurrence: z.nativeEnum(ListRecurrence).optional(),
		sourceType: z.nativeEnum(ListSourceType),
		maxProspects: z.string(),
		maxPerCompany: z.string(),
		campaigns: z.array(z.number()),
		getContactInformation: z.nativeEnum(GetContactInformation),
		onlyNewLeads: z.boolean(),
		findPersonContactMode: z.nativeEnum(FindPersonContactMode).optional(),
		getEmailInformation: z.nativeEnum(GetEmailInformation),
		getPhoneInformation: z.nativeEnum(GetPhoneInformation),
		maxDate: z.string().nullable().optional(),
		webhooks: z.array(ListWebhookSchema),
		priority: z.number().nullable().optional(),
		...ListIntervalSettingsSchema.shape,
		// Auto-assign to campaign after lead scoring
		autoAssignToCampaign: z.boolean().optional(),
		autoAssignMinScore: z.string().nullable().optional(),
		// Smart List fields
		smartList: z
			.object({
				frequency: z.nativeEnum(SmartListFrequency).optional(),
				filter: z.string().nullable().optional(),
				semanticSearchProspects: z.string().nullable().optional(),
				sources: z
					.array(
						z.object({
							id: z.number(),
							name: z.string(),
							filter: z.string().nullable().optional(),
							lastRunAt: z.string().nullable().optional(),
						})
					)
					.optional(),
			})
			.nullable()
			.optional(),
	})
	.refine(
		(data) => {
			if (
				data.maxProspects === "" &&
				data.sourceType !== ListSourceType.CSV_ENRICHMENTS &&
				data.sourceType !== ListSourceType.CUSTOM_EVENTS &&
				data.sourceType !== ListSourceType.JOB_CHANGE &&
				data.sourceType !== ListSourceType.SMART_LIST
			) {
				return false;
			}

			return true;
		},
		{
			message: "REQUIRED",
			path: ["maxProspects"],
		}
	)
	.refine(
		(data) => {
			if (data.autoAssignToCampaign && !data.autoAssignMinScore) {
				return false;
			}

			return true;
		},
		{
			message: "REQUIRED",
			path: ["autoAssignMinScore"],
		}
	);

export const UpdateListSchema = z.object({
	id: z.number(),
	name: z.string().min(4, { message: REQUIRED }).trim().optional(),
	recurrence: z.nativeEnum(ListRecurrence).optional(),
	maxProspects: z.number().nullable().optional(),
	apollos: z.array(z.number()),
	liEvents: z.array(z.number()),
	csvEnrichments: z.array(z.number()),
	northdatas: z.array(z.number()),
	customEvents: z.array(z.number()),
	liPeople: z.array(z.number()),
	liPosts: z.array(z.number()),
	liCompanyActivity: z.array(z.number()),
	liSn: z.array(z.number()),
	jobs: z.array(z.number()),
	liProfileVisits: z.array(z.number()),
	liProfile: z.array(z.number()),
	maps: z.array(z.number()),
	aiEnrichments: z.array(z.number()),
	campaigns: z.array(z.number()),
	scores: z.array(z.number()),
	updateType: z.nativeEnum(UpdateType),
	status: z.nativeEnum(ListStatus).optional(),
	columnId: z.number().optional(),
	aiApplyType: z.nativeEnum(AiApplyType).optional(),
	prospectIds: z.array(z.number()).optional(),
	parallelAi: z.array(ParallelAiSchema).optional(),
	saveAsTemplate: z.boolean().optional(),
	templateId: z.number().optional(),
	archived: z.boolean().optional(),
	scrapeLevel: z.nativeEnum(ScrapeLevel).nullable().optional(),
	enrichmentType: z.nativeEnum(EnrichmentType).nullable().optional(),
	filters: z
		.object({
			searchValue: z.string().optional(),
			assignStatus: z.array(z.nativeEnum(AssignStatus)).optional(),
			filter: z.record(z.string(), z.any()).optional(),
			inclusionFilterStatus: z.array(z.nativeEnum(InclusionFilterStatus)).optional(),
		})
		.optional(),
	locale: z.string().optional(),
	getContactInformation: z.nativeEnum(GetContactInformation),
	getEmailInformation: z.nativeEnum(GetEmailInformation),
	getPhoneInformation: z.nativeEnum(GetPhoneInformation),
	webhooks: z.array(ListWebhookSchema),
	priorityFilterStatus: z.array(z.nativeEnum(PriorityFilterStatus)).optional(),
	campaignConfig: z.any().nullable().optional(),
	campaignStatus: z.nativeEnum(CampaignStatus).nullable().optional(),
	campaignIdToUpdate: z.string().nullable().optional(),
	/** Which screen/action produced a campaign write; logged by the API for attribution (PREC-4026). */
	campaignWriteOrigin: z.string().optional(),
	...ListIntervalSettingsSchema.shape,
	// Per-list email deliverability overrides (PREC-4261); null = workspace default
	...ListEmailDeliverabilityOverridesSchema.shape,
	// HubSpot deal creation trigger (list-level override)
	hubspotDealCreationTrigger: z
		.nativeEnum(HubspotDealCreationTriggerWithInherit)
		.nullable()
		.optional(),
	// Auto-assign to campaign after lead scoring
	autoAssignToCampaign: z.boolean().optional(),
	autoAssignMinScore: z.string().nullable().optional(),
	// Smart List fields
	smartList: z
		.object({
			frequency: z.nativeEnum(SmartListFrequency).optional(),
			filter: z.string().nullable().optional(),
			semanticSearchProspects: z.string().nullable().optional(),
			sources: z
				.array(
					z.object({
						id: z.number(),
						name: z.string(),
						filter: z.string().nullable().optional(),
						lastRunAt: z.string().nullable().optional(),
					})
				)
				.optional(),
		})
		.nullable()
		.optional(),
});

export const ArchiveSchema = z.object({
	ids: z.array(z.number()),
	archived: z.boolean(),
});

export const UpdateSettingsListSchema = z.object({
	id: z.number(),
	name: z.string().min(4, { message: REQUIRED }).trim(),
	maxProspects: z.number().nullable(),
	type: z.nativeEnum(ListType),
	onlyNewLeads: z.boolean(),
	searchBase: z.nativeEnum(SearchBase),
	sourceType: z.nativeEnum(ListSourceType),
});

// Extracted (PREC-4026) because this file sits at the file-length ratchet; re-exported for importers.
export {
	ScoreCompanyCategoryInfoResponseSchema,
	ScoreCompanyCategoryInfoSchema,
} from "./score-company-category-info";
export type { ScoreCompanyCategoryInfo } from "./score-company-category-info";

export const FilterByValueSchema = z.object({
	data: z.array(
		z.object({
			value: z.string(),
			id: z.number().optional(),
		})
	),
});

export const SmartListSourceSchema = z.object({
	id: z.number(),
	smartListId: z.number().optional(),
	name: z.string(),
	filter: z.string().nullable().optional(),
	lastRunAt: z.string().nullable().optional(),
});

export type SmartListSource = z.infer<typeof SmartListSourceSchema>;

export const FilterSchema = z.object({
	events: z.array(
		z.object({
			liEventId: z.number(),
			liEventEntryId: z.number(),
			liEventEntryName: z.string(),
			attendees: z.number(),
			eventAt: z.string().transform((arg) => new Date(arg)),
		})
	),
	lists: z.array(
		z.object({
			listId: z.number(),
			listName: z.string(),
		})
	),
});

export const ListReportSchema = z.object({
	recordId: z.number().optional(),
	leadName: z.string().min(4, { message: REQUIRED }).trim().optional(),
	companyName: z.string().min(4, { message: REQUIRED }).trim().optional(),
	listName: z.string().min(4, { message: REQUIRED }).trim().optional(),
	listId: z.number().optional(),
	leadId: z.number().optional(),
	reportReason: z.string().min(4, { message: REQUIRED }).trim().optional(),
});

export const ListResponseSchema = z.object({
	data: z.array(ListSchema),
	meta: MetaDataSchema,
});

export const ListLogResponseSchema = z.object({
	data: z.array(ListLogSchema),
	meta: MetaDataSchema,
});

export const ListPersonResponseSchema = z.object({
	data: z.array(ListPersonSchema),
	meta: MetaDataSchema,
});

export const ListAiEnrichmentResponseSchema = z.object({
	data: z.array(ListAiEnrichmentSchema),
	meta: MetaDataSchema,
});

export const BulkAddPersonSyncItemSchema = z.object({
	prospectId: z.number().optional(),
	companyEnrichId: z.string().optional(),
	linkedinUrl: z.string().optional(),
	enrichEmail: z.boolean().optional(),
	enrichPhone: z.boolean().optional(),
});

export const BulkAddPersonsToSyncListResponseSchema = z.object({
	added: z.array(ListPersonSchema),
	// Items that are prospectIds of prospects already in the user's company (e.g. everything
	// resolved by from-linkedin-url) are added synchronously and returned here, with no
	// re-enrichment and no processingListId. Read this for already-resolved prospects.
	enriched: z.array(ListPersonSchema).optional(),
	failed: z.array(
		z.object({
			index: z.number(),
			reason: z.string(),
		})
	),
	// Set for URL/name items that still enrich asynchronously; poll this for their results.
	processingListId: z.number().nullable(),
});

// The LinkedIn-URL resolution schemas live in their own module (this one is over the length
// ratchet and may only shrink); re-exported so existing `schema/list` import sites keep working.
export * from "./list-bulk-actions.schema";
export * from "./list-linkedin-resolution.schema";

export const LemlistResponseSchema = z.object({
	data: z.array(LemlistSchema),
	meta: MetaDataSchema,
});

export const ListSourceResponseSchema = z.object({
	data: z.array(ListSourceSchema),
	meta: MetaDataSchema,
});

export const ListReportResponseSchema = z.object({
	data: z.array(ListSchema),
	meta: MetaDataSchema,
});

export type SaveListSettings = z.infer<typeof SaveListSettingsSchema>;

export type TrackerFileInfo = z.infer<typeof TrackerFileInfoSchema>;
export type TrackerInfo = z.infer<typeof TrackerInfoSchema>;
export type List = z.infer<typeof ListSchema>;
export type ListLog = z.infer<typeof ListLogSchema>;
export type ListSource = z.infer<typeof ListSourceSchema>;
export type ListAiEnrichment = z.infer<typeof ListAiEnrichmentSchema>;
export type Lemlist = z.infer<typeof LemlistSchema>;
export type LemlistAll = z.infer<typeof LemlistAllSchema>;
export type ListReport = z.infer<typeof ListReportSchema>;

export type CreateList = z.infer<typeof CreateListSchema>;
export type UpdateList = z.infer<typeof UpdateListSchema>;
export type UpdateSettingsList = z.infer<typeof UpdateSettingsListSchema>;

export type ListResponse = z.infer<typeof ListResponseSchema>;
export type ListReportResponse = z.infer<typeof ListReportResponseSchema>;
export type CustomColumn = z.infer<typeof CustomColumnSchema>;
export type ListLogResponse = z.infer<typeof ListLogResponseSchema>;
export type ListSourceResponse = z.infer<typeof ListSourceResponseSchema>;
export type ListAiEnrichmentResponse = z.infer<typeof ListAiEnrichmentResponseSchema>;
export type LemlistResponse = z.infer<typeof LemlistResponseSchema>;

export type ListPersonResponse = z.infer<typeof ListPersonResponseSchema>;
export type ListPerson = z.infer<typeof ListPersonSchema>;

export type BulkAddPersonSyncItem = z.infer<typeof BulkAddPersonSyncItemSchema>;
export type BulkAddPersonsToSyncListResponse = z.infer<
	typeof BulkAddPersonsToSyncListResponseSchema
>;

export type Archive = z.infer<typeof ArchiveSchema>;

export type Filter = z.infer<typeof FilterSchema>;

export type FilterByValue = z.infer<typeof FilterByValueSchema>;

export type BulkAssignResponse = z.infer<typeof BulkAssignResponseSchema>;

export type ArchiveResponse = z.infer<typeof ArchiveResponseSchema>;

export type ListWebhook = z.infer<typeof ListWebhookSchema>;

export type ListPersonOtherLists = z.infer<typeof ListPersonOtherListsSchema>;

export type ListEvent = z.infer<typeof ListEventSchema>;

export * from "./list-signal-config";
export * from "./update-list-details.schema";

export type {
	DownloadFile,
	DownloadFilteredRequestParams,
	GetUnassignedListRequestPaginationParams,
	ListRequestPaginationParams,
	ListViewReqiestPaginationParams,
} from "./list-request-params";

/** Aggregated inbox reply counts for the My / Team / All reply tabs. */
export const ReplyCountsSchema = z.object({
	myReplies: z.number(),
	teamReplies: z.number(),
	allReplies: z.number(),
});

export type ReplyCounts = z.infer<typeof ReplyCountsSchema>;

// Bulk tracker/webhook/contact-search schemas live in a sibling file (500-line ratchet);
// re-exported so consumers keep importing from "../schema/list".
export * from "./listBulk";

export * from "./organization-profiles";
